import React, { cloneElement } from 'react';
import { useListNavigation } from './use-list-navigation';

/**
 * @typedef {Object} ListItemsNavigatorProps
 * @property {'vertical'|'horizontal'} [orientation='vertical'] - The navigation direction
 * @property {boolean} [loop=true] - Whether navigation should loop around
 * @property {boolean} [disabled=false] - Disables keyboard navigation
 * @property {Function} [onItemActivate] - Callback when an item is activated (Enter/Space)
 * @property {Function} [onItemFocus] - Callback when an item receives focus
 * @property {Object} [itemOverrides] - Props to override on each item (e.g. common event handlers)
 * @property {React.ReactNode} children - List items to navigate
 */

/**
 * ListItemsNavigator - A component for keyboard navigation through list items
 * @param {ListItemsNavigatorProps & React.HTMLProps<HTMLDivElement>} props
 */
export function ListItemsNavigator({
  children,
  orientation = 'vertical',
  loop = true,
  disabled = false,
  onItemActivate,
  onItemFocus,
  itemOverrides = {},
  className,
  ...props
}) {
  const { items, getItemProps, getContainerProps } = useListNavigation({
    children,
    orientation,
    loop,
    disabled,
    onItemActivate,
    onItemFocus,
  });

  // Clone each child with navigation props
  const enhancedChildren = items.map((child, index) => {
    if (!React.isValidElement(child)) {
      return child;
    }

    const itemProps = getItemProps(index);

    // In React 19, ref is now a regular prop, so we need to preserve it explicitly
    return cloneElement(child, {
      ...itemProps,
      ...itemOverrides,
      key: child.key || index,
      ref: child.props.ref,
    });
  });

  return (
    <div {...props} {...getContainerProps()} className={className}>
      {enhancedChildren}
    </div>
  );
}
